import {CommonModule} from "@angular/common"
import {
    ChangeDetectionStrategy,
    Component,
    computed,
    DestroyRef,
    effect,
    inject,
    input,
    model,
    OnDestroy,
    output,
    signal,
    untracked,
    WritableSignal,
} from "@angular/core"
import type {CmLogger} from "@cm/log"
import {CM_LOGGER_TEXTURES} from "@common/services/logging"
import {takeUntilDestroyed} from "@angular/core/rxjs-interop"
import {MatDialog, MatDialogRef} from "@angular/material/dialog"
import {MatMenuModule} from "@angular/material/menu"
import {MatTooltipModule} from "@angular/material/tooltip"
import {ActivatedRoute, Router, RouterModule} from "@angular/router"
import {ButtonComponent} from "@app/common/components/buttons/button/button.component"
import {HintComponent, HintTypes} from "@app/common/components/hint/hint.component"
import {LinkedDimensionInputComponent} from "@app/common/components/inputs/linked-dimension-input/linked-dimension-input.component"
import {NgVar} from "@app/common/directives"
import {RefreshService} from "@app/common/services/refresh/refresh.service"
import {UploadGqlService} from "@app/common/services/upload/upload.gql.service"
import {
    QueryTextureSetRevisionViewRevisionDataGQL,
    QueryTextureSetRevisionViewTextureSetDataGQL,
    TextureSetRevisionViewCreateTextureSetRevisionGQL,
    TextureSetRevisionViewTextureSetFragment,
} from "@app/textures/texture-set-revision-view/texture-set-revision-view.generated"
import {imageDataTypeFromTextureType} from "@app/textures/utils/color-space"
import {descriptorByTextureType, textureTypes} from "@app/textures/utils/texture-type-descriptor"
import {Size2Like} from "@cm/math"
import {InputContainerComponent} from "@common/components/inputs/input-container/input-container.component"
import {StringInputComponent} from "@common/components/inputs/string-input/string-input.component"
import {fetchThrowingErrors} from "@common/helpers/api/fetch"
import {mutateThrowingErrors} from "@common/helpers/api/mutate"
import {NotificationsService} from "@common/services/notifications/notifications.service"
import {ContentTypeModel, DataObjectType, TextureType} from "@generated"
import {MimeType} from "@legacy/helpers/utils"
import {TextureThumbnailViewComponent} from "app/textures/texture-set-revision-view/texture-thumbnail-view/texture-thumbnail-view.component"
import {filter, firstValueFrom} from "rxjs"
import {UploadProcessingService} from "@common/services/upload/upload-processing.service"
import {PermissionsService} from "@common/services/permissions/permissions.service"
import {
    UploadTextureDisplacementDialogComponent,
    UploadTextureDisplacementDialogResult,
} from "@app/textures/texture-set-revision-view/upload-texture-displacement-dialog/upload-texture-displacement-dialog.component"
import {TexturesApiService} from "@app/textures/service/textures-api.service"

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-texture-set-revision-view",
    templateUrl: "./texture-set-revision-view.component.html",
    styleUrl: "./texture-set-revision-view.component.scss",
    imports: [
        CommonModule,
        MatTooltipModule,
        MatMenuModule,
        RouterModule,
        NgVar,
        TextureThumbnailViewComponent,
        HintComponent,
        ButtonComponent,
        LinkedDimensionInputComponent,
        StringInputComponent,
        InputContainerComponent,
    ],
})
export class TextureSetRevisionViewComponent implements OnDestroy {
    private readonly logger: CmLogger = inject(CM_LOGGER_TEXTURES)
    readonly $textureSetId = input<string>(undefined, {alias: "textureSetId"})

    readonly $textureSetRevisionId = input<string | undefined>(undefined, {alias: "textureSetRevisionId"})

    readonly $canEdit = input<boolean>(false, {alias: "canEdit"})
    readonly $canDownload = input<boolean>(false, {alias: "canDownload"})
    readonly $showEmptyTextures = model<boolean | undefined>(undefined, {alias: "showEmptyTextures"})
    readonly $canSelectRevisionId = input<boolean>(true, {alias: "canSelectRevisionId"})
    readonly showEmptyTexturesChange = output<boolean>()

    readonly queryTextureSetViewTextureSetData = inject(QueryTextureSetRevisionViewTextureSetDataGQL)
    readonly queryTextureSetRevisionViewRevisionData = inject(QueryTextureSetRevisionViewRevisionDataGQL)
    readonly createTextureSetRevisionGql = inject(TextureSetRevisionViewCreateTextureSetRevisionGQL)

    private readonly permission = inject(PermissionsService)
    private readonly $can = this.permission.$to
    private readonly destroyRef = inject(DestroyRef)
    private readonly router = inject(Router)
    private readonly route = inject(ActivatedRoute)
    private readonly refreshService = inject(RefreshService)
    private readonly uploadService = inject(UploadGqlService)
    private readonly uploadProcessingService = inject(UploadProcessingService)
    private readonly notificationService = inject(NotificationsService)
    private readonly dialog = inject(MatDialog)
    private readonly texturesApi = inject(TexturesApiService)

    constructor() {
        effect(() => {
            const textureSetRevisionId = this.$textureSetRevisionId()
            untracked(() => this.loadTextureSetRevision(textureSetRevisionId))
        })
        this.refreshService.itemSubject
            .pipe(
                filter(({id}) => id === this._textureSetRevisionId),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe(({id}) => this.loadTextureSetRevision(id))
    }

    ngOnDestroy() {
        this.cleanUpAndReset(this.$_data())
        this.$data.set(undefined)
    }

    private async loadTextureSetRevision(textureSetRevisionId: string | undefined) {
        this.cleanUpAndReset(this.$_data())
        this.$data.set(undefined)
        const textureSetId = this.$textureSetId()
        if (!textureSetId) {
            throw new Error("Texture set ID is not set")
        }
        this._textureSetRevisionId = textureSetRevisionId

        const textureSet = await fetchThrowingErrors(this.queryTextureSetViewTextureSetData)({id: textureSetId}).then((result) => result.textureSet)

        let textureSetRevisionInfo: TextureSetRevisionInfo
        const isExistingTextureSetRevision = textureSetRevisionId !== undefined
        if (isExistingTextureSetRevision) {
            const result = await fetchThrowingErrors(this.queryTextureSetRevisionViewRevisionData)({id: textureSetRevisionId})
            if (result.textureSetRevision.textureSet.id !== textureSetId) {
                throw new Error("Texture set revision does not belong to the texture set")
            }
            const dataObjectByTextureType = new Map(
                result.textureSetRevision.mapAssignments.map((mapAssignment) => [mapAssignment.textureType, mapAssignment.dataObject]),
            )
            this._originalDataObjectByTextureType = new Map(
                Array.from(dataObjectByTextureType.entries()).map(([textureType, dataObject]) => [textureType, {id: dataObject.id}]),
            )
            this._originalName = result.textureSetRevision.name ?? ""
            textureSetRevisionInfo = {
                width: result.textureSetRevision.width,
                height: result.textureSetRevision.height,
                displacement: result.textureSetRevision.displacement ?? undefined,
            }
        } else {
            textureSetRevisionInfo = {
                width: 30,
                height: 30,
                displacement: undefined,
            }
        }

        this.$data.set({
            textureSet,
            dataObjectByTextureType: new Map(this._originalDataObjectByTextureType),
            name: this._originalName,
            changed: false,
            textureSetRevisionInfo,
            uploadedDataObjectByTextureType: new Map(),
            pendingUploads: new Map(),
            newWidthEntered: isExistingTextureSetRevision,
            newHeightEntered: isExistingTextureSetRevision,
        })
    }

    readonly $allowTextureClick = computed(() => {
        const data = this.$_data()
        return this.$canEdit() && data != null && !data.changed
    })

    readonly $textureEditorUrl = computed(() => "set-revisions")

    protected readonly $shouldShowEmptyTextures = computed(() => {
        const showEmptyTextures = this.$showEmptyTextures()
        if (showEmptyTextures !== undefined) {
            return showEmptyTextures
        } else {
            return this.$_data()?.dataObjectByTextureType.size === 0
        }
    })

    protected toggleShowEmptyTextures() {
        const value = !this.$shouldShowEmptyTextures()
        this.$showEmptyTextures.set(value)
        this.showEmptyTexturesChange.emit(value)
    }

    protected isUploading(textureType: TextureType): boolean {
        return this.$_data()?.pendingUploads.has(textureType) ?? false
    }

    protected getDataObjectId(textureType: TextureType): string | undefined {
        return this.$_data()?.dataObjectByTextureType.get(textureType)?.id
    }

    protected onTextureDropped(textureType: TextureType, file: File) {
        const data = this.$_data()
        if (!data) {
            return
        }
        this.uploadNewTextureRevision(data, textureType, file)
    }

    protected onTextureClicked(_textureType: TextureType) {
        if (this.$_data()?.changed) {
            // we're in uploading mode; let's choose a file for upload
        }
    }

    protected onThumbnailAvailable(textureType: TextureType, dataObject: {id: string; width?: number | null; height?: number | null}) {
        const mapsToCheck = [this._originalDataObjectByTextureType, this.$_data()?.dataObjectByTextureType, this.$_data()?.uploadedDataObjectByTextureType]
        let dataObjectSizeUpdated = false
        mapsToCheck.forEach((map) => {
            const dataObjectInfo = map?.get(textureType)
            if (dataObjectInfo && dataObjectInfo.id === dataObject.id) {
                dataObjectInfo.width = dataObject.width ?? undefined
                dataObjectInfo.height = dataObject.height ?? undefined
                dataObjectSizeUpdated = true
            }
        })
        if (dataObjectSizeUpdated) {
            this.notifyDataChanged()
            this.updateStatus()
        }
    }

    private updateStatus() {
        const data = this.$_data()
        if (!data) {
            return
        }
        if (data.pendingUploads.size === 0 && data.uploadedDataObjectByTextureType.size === 0) {
            this.$status.set("viewing")
            return
        }
        if (data.pendingUploads.size > 0) {
            this.$status.set("processing")
            return
        }
        let firstSize: Size2Like | undefined = undefined
        for (const info of data.dataObjectByTextureType.values()) {
            if (info.width == null || info.height == null) {
                this.$status.set("processing")
                return
            }
            if (!firstSize) {
                firstSize = {width: info.width, height: info.height}
            } else if (firstSize.width !== info.width || firstSize.height !== info.height) {
                this.$status.set("inconsistent-sizes")
                return
            }
        }
        this.$status.set("valid")
    }

    readonly $needsSave = computed(() => this.$_data()?.changed === true)

    protected onTextureDeleteClicked(textureType: TextureType) {
        const data = this.$_data()
        if (!data) {
            return
        }
        this.removeUploadedDataObject(data, textureType)
        data.uploadedDataObjectByTextureType.set(textureType, null)
        this.notifyDataChanged()
        this.updateStatus()
    }

    protected readonly $textureSetRevisionWidth = computed(() => {
        const data = this.$_data()
        return data?.newWidthEntered ? data.textureSetRevisionInfo.width : undefined
    })

    protected setTextureSetRevisionWidth(value: number | null | undefined) {
        const data = this.$_data()
        if (!data) {
            throw new Error("Data is not set")
        }
        if (value != null && value > 0) {
            data.textureSetRevisionInfo.width = value
            data.newWidthEntered = true
        } else {
            data.newWidthEntered = false
        }
        this.notifyDataChanged()
    }

    protected readonly $textureSetRevisionHeight = computed(() => {
        const data = this.$_data()
        return data?.newHeightEntered ? data.textureSetRevisionInfo.height : undefined
    })

    protected setTextureSetRevisionHeight(value: number | null | undefined) {
        const data = this.$_data()
        if (!data) {
            throw new Error("Data is not set")
        }
        if (value != null && value > 0) {
            data.textureSetRevisionInfo.height = value
            data.newHeightEntered = true
        } else {
            data.newHeightEntered = false
        }
        this.notifyDataChanged()
    }

    protected readonly $imageAspectRatio = computed<number | undefined>(() => {
        // "valid" covers a just-finished upload; "viewing" covers an existing revision's already-loaded,
        // already-consistent textures (e.g. editing physical size without re-uploading anything) — both
        // are settled states where the texture dimensions are known. "processing"/"inconsistent-sizes" aren't.
        const status = this.$status()
        if (status !== "valid" && status !== "viewing") {
            return undefined
        }
        const first = this.$_data()?.dataObjectByTextureType.values().next().value
        return first?.width && first?.height ? first.width / first.height : undefined
    })

    protected readonly $physicalDimensionsSet = computed(() => {
        const data = this.$_data()
        if (!data) {
            return false
        }
        return data.newWidthEntered && data.newHeightEntered
    })

    protected readonly $canSave = computed(() => {
        const data = this.$_data()
        if (!data) {
            return false
        }
        return data.changed && data.dataObjectByTextureType?.size > 0 && this.$status() === "valid" && this.$physicalDimensionsSet()
    })

    protected updateDataName(name: string): void {
        const data = this.$_data()
        if (data) {
            data.name = name
            this.notifyDataChanged()
        }
    }

    protected onRevert() {
        this.cleanUpAndReset(this.$_data())
    }

    protected onSave() {
        this.save(false)
    }

    protected onSaveAndEdit() {
        this.save(true)
    }

    protected async save(navigateToEditor: boolean) {
        const data = this.$_data()
        if (!data) {
            return
        }
        const textureTypeAndDataObjects = Array.from(data.dataObjectByTextureType.entries())
        if (textureTypeAndDataObjects.length === 0) {
            return
        }
        if (!data.textureSetRevisionInfo) {
            throw new Error("Texture set revision info is missing")
        }
        // create new texture set revision
        const textureSetRevisionId = await mutateThrowingErrors(this.createTextureSetRevisionGql)({
            input: {
                name: data.name,
                width: data.textureSetRevisionInfo.width,
                height: data.textureSetRevisionInfo.height,
                displacement: data.textureSetRevisionInfo.displacement,
                textureSetId: data.textureSet.id,
                mapAssignments: textureTypeAndDataObjects.map(([textureType, dataObject]) => ({
                    textureType,
                    dataObjectId: dataObject.id,
                })),
            },
        }).then((result) => result.createTextureSetRevision.id)
        // clear changed state
        data.uploadedDataObjectByTextureType.clear() // clear uploaded textures list to prevent them from being cleaned up
        data.changed = false
        this.notifyDataChanged()

        if (navigateToEditor) {
            this.navigateToRevision(textureSetRevisionId, undefined)
        }

        // broadcast refresh event
        this.refreshService.item({id: data.textureSet.id, __typename: ContentTypeModel.TextureSet})
    }

    private navigateToRevision(textureSetRevisionId: string | undefined, textureType: TextureType | undefined) {
        if (!this.$_data()) {
            return
        }
        this.router.navigate(["set-revisions", textureSetRevisionId], {
            relativeTo: this.route,
            queryParams: {
                textureType: textureType,
            },
            queryParamsHandling: "merge",
        })
    }

    readonly $canManuallyEnableEditMode = computed(() => this.$can().create.manuallyEnableTextureSetRevisionEditMode())

    protected enableEditMode() {
        const data = this.$_data()
        if (!data) {
            return
        }
        this.setDataChanged(data)
        this.notifyDataChanged()
    }

    private setDataChanged(data: Data) {
        if (!data.changed) {
            data.name = this._originalName ? this._originalName + " (modified)" : "Uploaded revision"
            data.changed = true
        }
    }

    private removeUploadedDataObject(data: Data, textureType: TextureType) {
        const previouslyUploadedTextureRevision = data.uploadedDataObjectByTextureType.get(textureType)
        if (previouslyUploadedTextureRevision) {
            this.revertUploadedDataObject(previouslyUploadedTextureRevision)
            data.uploadedDataObjectByTextureType.delete(textureType)
        }
        data.dataObjectByTextureType.delete(textureType)
        this.notifyDataChanged()
    }

    private async uploadNewTextureRevision(data: Data, textureType: TextureType, file: File): Promise<void> {
        let displacement: number | undefined = undefined
        if (textureType === TextureType.Displacement) {
            // show dialog to select texture properties
            const dialogRef: MatDialogRef<UploadTextureDisplacementDialogComponent, UploadTextureDisplacementDialogResult | undefined> = this.dialog.open(
                UploadTextureDisplacementDialogComponent,
                {
                    width: "350px",
                    data: {
                        displacement: data.textureSetRevisionInfo?.displacement,
                    },
                },
            )
            const result = await firstValueFrom(dialogRef.afterClosed())
            if (!result) {
                return
            }
            displacement = result.displacement
        }

        // if this is the first texture and we automatically show empty textures when there are none, switch to showing empty textures explicitly such that the view doesn't suddenly hides them
        if (data.dataObjectByTextureType.size === 0 && this.$showEmptyTextures() === undefined) {
            this.$showEmptyTextures.set(true)
            this.showEmptyTexturesChange.emit(true)
        }

        try {
            this.setDataChanged(data)

            // set new displacement value if given
            if (displacement !== undefined) {
                data.textureSetRevisionInfo!.displacement = displacement
            }

            // remove previously uploaded texture revision
            this.removeUploadedDataObject(data, textureType)

            // upload data object
            // TODO allow cancellation (currently not supported by the upload service)
            const pendingUpload = this.uploadService
                .createAndUploadDataObject(
                    file,
                    {
                        organizationLegacyId: data.textureSet.textureGroup.organization.legacyId,
                        type: DataObjectType.Texture,
                        imageDataType: imageDataTypeFromTextureType(textureType),
                    },
                    {processUpload: false, showUploadToolbar: true},
                )
                .then(async (dataObject) => {
                    const thumbnailJob = await this.uploadProcessingService.createUploadProcessingJob(dataObject.id)
                    return {...dataObject, thumbnailProcessingJobId: thumbnailJob.id}
                })
            data.pendingUploads.set(textureType, pendingUpload)
            this.notifyDataChanged()
            this.updateStatus()
            const dataObject = await pendingUpload

            data.uploadedDataObjectByTextureType.set(textureType, {
                id: dataObject.id,
            })
            data.dataObjectByTextureType.set(textureType, {id: dataObject.id})
            this.notifyDataChanged()
            this.updateStatus()
        } catch (e: unknown) {
            let message: string
            if (typeof e === "string") {
                message = e
            } else if (e instanceof Error) {
                message = e.message
            } else {
                message = "Unknown error"
            }
            this.notificationService.showError("Error uploading texture: " + message)
        } finally {
            data.pendingUploads.delete(textureType)
            this.notifyDataChanged()
            this.updateStatus()
            // TODO: there is probably a better way to do this
            if (data != this.$_data()) {
                // some new data was loaded in the meantime, lets clean up and reset
                this.cleanUpAndReset(data)
            }
        }
    }

    private async revertUploadedDataObject(uploadedDataObjectInfo: DataObjectInfo | null) {
        if (uploadedDataObjectInfo) {
            this.logger.debug(`Cleaning up data-object ${uploadedDataObjectInfo.id}...`)
            await this.texturesApi.deleteDataObject(uploadedDataObjectInfo.id)
        }
    }

    private cleanUpAndReset(data: Data | undefined) {
        if (!data) {
            return
        }
        for (const textureRevisionInfo of data.uploadedDataObjectByTextureType.values()) {
            void this.revertUploadedDataObject(textureRevisionInfo)
        }
        data.uploadedDataObjectByTextureType.clear()
        data.dataObjectByTextureType = new Map(this._originalDataObjectByTextureType)
        data.name = this._originalName
        data.changed = false
        this.notifyDataChanged()
    }

    protected HintTypes = HintTypes
    readonly $textureTypes = signal(textureTypes)
    readonly $descriptorByTextureType = signal(descriptorByTextureType)
    readonly $data = signal<Data | undefined>(undefined)
    protected readonly $status: WritableSignal<Status> = signal("viewing")
    private readonly $_dataVersion = signal(0)
    protected readonly $_data = computed(
        () => {
            this.$_dataVersion()
            return this.$data()
        },
        {equal: () => false},
    )
    private notifyDataChanged() {
        this.$_dataVersion.update((v) => v + 1)
    }

    private _textureSetRevisionId: string | undefined
    private _originalDataObjectByTextureType: Map<TextureType, DataObjectInfo> | undefined = undefined
    private _originalName: string = ""
    protected readonly MimeType = MimeType
}

type TextureSetRevisionInfo = {
    width: number
    height: number
    displacement?: number
}

type DataObjectInfo = {
    id: string
    width?: number
    height?: number
}

type Data = {
    textureSet: TextureSetRevisionViewTextureSetFragment
    dataObjectByTextureType: Map<TextureType, DataObjectInfo>
    name: string
    changed: boolean
    textureSetRevisionInfo: TextureSetRevisionInfo
    uploadedDataObjectByTextureType: Map<TextureType, DataObjectInfo | null> // null means deletion of previously assigned texture
    pendingUploads: Map<TextureType, Promise<unknown>>
    newWidthEntered: boolean
    newHeightEntered: boolean
}

type Status = "viewing" | "processing" | "inconsistent-sizes" | "valid"
